"use client";
import { MarketingCard } from "@web/components/MarketingShell";
import {
  BlogPreviewMetadata,
  createSlugForPost,
  getRecent,
} from "@web/data/blogs";
import Link from "next/link";
import { useState } from "react";

function ReadArticleLink({ slug }: { slug: string }) {
  return (
    <Link
      href={`/news/${slug}`}
      className="mt-6 inline-flex h-[44px] items-center justify-center rounded-md border border-[#0f1219] bg-[#0f1219] px-5 text-sm font-semibold font-clash uppercase text-white shadow-[0_14px_28px_rgba(15,18,25,0.14)] transition-all duration-200 hover:bg-[#1b1f2d]"
    >
      Read article
    </Link>
  );
}

function BlogPreviewSmall({ metadata }: { metadata: BlogPreviewMetadata }) {
  const slug = createSlugForPost(metadata.title);

  return (
    <MarketingCard contentClassName="flex h-full flex-col p-4 sm:p-5">
      <img
        alt={metadata.title}
        src={metadata.image}
        className="aspect-[588/366] w-full rounded-[1.2rem] border border-white/80 bg-zinc-300 object-cover shadow-[0_20px_40px_rgba(34,50,120,0.08)]"
      />
      <div className="mt-5 flex flex-1 flex-col">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="rounded-full bg-white/80 px-3 py-1 text-[11px] font-semibold font-clash uppercase tracking-[0.16em] text-[#475085]">
            News update
          </div>
          <div className="text-xs font-semibold font-clash uppercase tracking-[0.16em] text-[#69708a]">
            {metadata.date}
          </div>
        </div>
        <div className="mt-4 text-[24px] font-semibold font-clash uppercase leading-tight text-[#0f1219] sm:text-[28px]">
          {metadata.title}
        </div>
        <div className="mt-3 max-h-[84px] overflow-hidden text-sm leading-7 text-[#4c556b]">
          {metadata.description}
        </div>
        <div className="flex-grow" />
        <ReadArticleLink slug={slug} />
      </div>
    </MarketingCard>
  );
}

function BlogPreviewBig({ metadata }: { metadata: BlogPreviewMetadata }) {
  const slug = createSlugForPost(metadata.title);

  return (
    <MarketingCard featured contentClassName="p-4 sm:p-6 lg:p-8">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)] lg:items-center">
        <div className="flex flex-col">
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-full bg-white/80 px-3 py-1 text-[11px] font-semibold font-clash uppercase tracking-[0.16em] text-[#475085]">
              Featured story
            </div>
            <div className="text-xs font-semibold font-clash uppercase tracking-[0.16em] text-[#69708a]">
              {metadata.date}
            </div>
          </div>
          <div className="mt-5 max-w-3xl text-[30px] font-semibold font-clash uppercase leading-tight text-[#0f1219] sm:text-[38px] lg:text-[46px]">
            {metadata.title}
          </div>
          <div className="mt-4 max-w-2xl text-base leading-8 text-[#4c556b]">
            {metadata.description}
          </div>
          <ReadArticleLink slug={slug} />
        </div>
        <img
          alt={metadata.title}
          src={metadata.image}
          className="aspect-[588/366] w-full rounded-[1.4rem] border border-white/80 bg-zinc-300 object-cover shadow-[0_24px_44px_rgba(34,50,120,0.12)]"
        />
      </div>
    </MarketingCard>
  );
}

export function BlogList({
  init,
}: {
  init: { items: BlogPreviewMetadata[]; hasNextPage: boolean };
}) {
  const [hasNextPage, setHasNextPage] = useState(init.hasNextPage);
  const [blogs, setBlogs] = useState(init.items);
  const [page, setPage] = useState(0);
  const [disabled, setDisabled] = useState(false);

  if (!blogs.length) {
    return (
      <MarketingCard contentClassName="p-6 sm:p-8">
        <div className="text-[11px] font-semibold font-clash uppercase tracking-[0.16em] text-[#69708a]">
          News unavailable
        </div>
        <div className="mt-4 max-w-2xl text-base leading-8 text-[#4c556b]">
          The news feed is temporarily unavailable. Please try again later.
        </div>
      </MarketingCard>
    );
  }

  return (
    <div className="space-y-12">
      <div>{blogs[0] ? <BlogPreviewBig metadata={blogs[0]} /> : <></>}</div>
      <div className="h-px w-full bg-[linear-gradient(90deg,rgba(59,130,246,0)_0%,rgba(59,130,246,0.8)_50%,rgba(59,130,246,0)_100%)]" />

      <div className="grid gap-7 lg:grid-cols-2">
        {blogs.slice(1).map((metadata, idx) => (
          <BlogPreviewSmall key={idx} metadata={metadata} />
        ))}
      </div>
      <div className="flex justify-center pt-2">
        {hasNextPage && (
          <button
            disabled={disabled}
            className="rounded-full border border-white/45 bg-white/85 px-7 py-3 text-sm font-semibold font-clash uppercase text-[#0f1219] shadow-[0_18px_40px_rgba(34,50,120,0.08)] backdrop-blur-sm transition-all duration-200 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => {
              setDisabled(true);
              fetch(`/api/blog`, {
                method: "POST",
                body: JSON.stringify({ page: page + 1 }),
                headers: {
                  "content-type": "application/json",
                },
              })
                .then(async (res) => {
                  if (res.status !== 200) {
                    console.error(await res.text());
                    setHasNextPage(false);
                    return;
                  }
                  const response = (await res.json()) as Awaited<
                    ReturnType<typeof getRecent>
                  >;
                  setPage((page) => page + 1);
                  setHasNextPage(response.hasNextPage);
                  setBlogs((blogs) => blogs.concat(response.items));
                })
                .finally(() => setDisabled(false));
            }}
          >
            Show more
          </button>
        )}
      </div>
    </div>
  );
}
